import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['hsl(188, 72%, 28%)', 'hsl(199, 89%, 48%)', 'hsl(36, 95%, 52%)', 'hsl(158, 64%, 39%)', 'hsl(2, 78%, 50%)'];

const Reportes = () => {
  const [stats, setStats] = useState(null);
  const [vehiculos, setVehiculos] = useState([]);
  const [ofertas, setOfertas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, vehiculosRes, ofertasRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/vehiculos`),
        axios.get(`${API}/ofertas`),
      ]);
      setStats(statsRes.data);
      setVehiculos(vehiculosRes.data);
      setOfertas(ofertasRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="text-muted-foreground">Cargando...</div></div>;
  }

  // Ofertas por día (dummy data - en producción usar datos reales)
  const ofertasPorDia = [
    { day: '10/10', offers: 3 },
    { day: '11/10', offers: 5 },
    { day: '12/10', offers: 4 },
    { day: '13/10', offers: 7 },
    { day: '14/10', offers: 6 },
  ];

  // Estados de publicaciones
  const estadosData = [
    { name: 'Borrador', value: vehiculos.filter((v) => v.estadoPublicacion === 'Borrador').length },
    { name: 'Enviada', value: vehiculos.filter((v) => v.estadoPublicacion === 'Enviada').length },
    { name: 'Adjudicada', value: vehiculos.filter((v) => v.estadoPublicacion === 'Adjudicada').length },
  ];

  // Distribución por marca
  const marcasCount = {};
  vehiculos.forEach((v) => {
    marcasCount[v.marca] = (marcasCount[v.marca] || 0) + 1;
  });
  const marcasData = Object.entries(marcasCount).map(([name, value]) => ({ name, value }));

  // Comparación precio base vs adjudicado
  const preciosComparacion = vehiculos
    .filter((v) => v.estadoPublicacion === 'Adjudicada' && v.precioAdjudicado)
    .map((v) => ({
      nombre: `${v.marca} ${v.modelo}`,
      base: v.precioBase,
      adjudicado: v.precioAdjudicado,
    }));

  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-semibold tracking-tight">Reportes y Analítica</h1>
          <p className="text-muted-foreground mt-1">Visualización de datos y métricas</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Tabs defaultValue="general" className="space-y-6 bg-background min-h-screen">
          <TabsList>
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="ofertas">Ofertas</TabsTrigger>
            <TabsTrigger value="vehiculos">Vehículos</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-6 bg-background min-h-screen">
            <div className="grid gap-6 sm:grid-cols-2">
              {/* KPIs */}
              <Card>
                <CardHeader>
                  <CardTitle>Totales</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-muted-foreground">Publicaciones</div>
                      <div className="text-2xl font-bold">{stats?.total_publicaciones || 0}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Ofertas</div>
                      <div className="text-2xl font-bold">{stats?.total_ofertas || 0}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Adjudicados</div>
                      <div className="text-2xl font-bold">{stats?.vehiculos_adjudicados || 0}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Tasa Adjudicación</div>
                      <div className="text-2xl font-bold">{stats?.tasa_adjudicacion || 0}%</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Estados */}
              <Card>
                <CardHeader>
                  <CardTitle>Estados de Publicaciones</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64" data-testid="chart-status-bar">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={estadosData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value" fill="hsl(var(--primary))" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="ofertas" className="space-y-6 bg-background min-h-screen">
            <Card>
              <CardHeader>
                <CardTitle>Ofertas por Día</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80" data-testid="chart-offers-by-day">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={ofertasPorDia}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="offers" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {preciosComparacion.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Precio Base vs Adjudicado</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={preciosComparacion}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="nombre" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="base" fill="hsl(var(--muted-foreground))" name="Precio Base" />
                        <Bar dataKey="adjudicado" fill="hsl(var(--primary))" name="Precio Adjudicado" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="vehiculos" className="space-y-6 bg-background min-h-screen">
            {marcasData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Distribución por Marca</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-80" data-testid="chart-brand-pie">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={marcasData} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} outerRadius={80} fill="#8884d8" dataKey="value">
                          {marcasData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Resumen de Vehículos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-muted-foreground">Promedio Km</div>
                      <div className="text-xl font-bold">
                        {vehiculos.length > 0
                          ? Math.round(vehiculos.reduce((sum, v) => sum + v.km, 0) / vehiculos.length).toLocaleString()
                          : 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Año Promedio</div>
                      <div className="text-xl font-bold">
                        {vehiculos.length > 0
                          ? Math.round(vehiculos.reduce((sum, v) => sum + v.anio, 0) / vehiculos.length)
                          : 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Precio Promedio</div>
                      <div className="text-xl font-bold">
                        ${vehiculos.length > 0
                          ? Math.round(vehiculos.reduce((sum, v) => sum + v.precioBase, 0) / vehiculos.length).toLocaleString('en-US')
                          : 0}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Reportes;